import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { CreditBalance, CreditPool, UserProfile } from "@chuzi/shared/types";
import {
  ChuziApiError,
  insufficientCreditsPool,
  isInsufficientCreditsError,
} from "@chuzi/shared/api";
import { dispatchCloseAllModals } from "./close-all-modals";
import { SpendingModal } from "../components/spending-modal";

export interface SpendingModalRequest {
  pool: CreditPool;
  creditsNeeded?: number;
  onSuccess?: (balance: CreditBalance) => void;
}

interface SpendingModalContextValue {
  openSpendingModal: (request: SpendingModalRequest) => void;
  closeSpendingModal: () => void;
}

const SpendingModalContext = createContext<SpendingModalContextValue | null>(null);

export function SpendingModalProvider({
  user,
  onBalanceUpdated,
  children,
}: {
  user: UserProfile | null;
  onBalanceUpdated?: (balance: CreditBalance) => void;
  children: ReactNode;
}) {
  const [request, setRequest] = useState<SpendingModalRequest | null>(null);

  const openSpendingModal = useCallback((next: SpendingModalRequest) => {
    dispatchCloseAllModals();
    // Defer so close handlers (e.g. exit scene editor) run before the modal mounts.
    requestAnimationFrame(() => {
      setRequest(next);
    });
  }, []);

  const closeSpendingModal = useCallback(() => {
    setRequest(null);
  }, []);

  const value = useMemo(
    () => ({ openSpendingModal, closeSpendingModal }),
    [openSpendingModal, closeSpendingModal],
  );

  return (
    <SpendingModalContext.Provider value={value}>
      {children}
      {request ? (
        <SpendingModal
          user={user}
          pool={request.pool}
          creditsNeeded={request.creditsNeeded}
          onClose={closeSpendingModal}
          onSuccess={(balance) => {
            onBalanceUpdated?.(balance);
            request.onSuccess?.(balance);
            closeSpendingModal();
          }}
        />
      ) : null}
    </SpendingModalContext.Provider>
  );
}

export function useSpendingModal(): SpendingModalContextValue {
  const ctx = useContext(SpendingModalContext);
  if (!ctx) {
    throw new Error("useSpendingModal must be used within SpendingModalProvider");
  }
  return ctx;
}

export function buildSpendingModalRequest(
  user: UserProfile | null,
  pool: CreditPool,
  creditsNeeded: number,
): SpendingModalRequest | null {
  if (!user) {
    return null;
  }
  return { pool, creditsNeeded };
}

export async function shouldOpenSpendingModal(
  err: unknown,
  user: UserProfile | null,
): Promise<SpendingModalRequest | null> {
  if (!isInsufficientCreditsError(err) || !user) {
    return null;
  }

  const pool = insufficientCreditsPool(err) ?? "create";
  const body = err.body as {
    credits_needed?: number;
  };
  const creditsNeeded =
    typeof body.credits_needed === "number" && body.credits_needed > 0
      ? body.credits_needed
      : 1;

  return buildSpendingModalRequest(user, pool, creditsNeeded);
}

export async function handleInsufficientCredits(
  err: unknown,
  user: UserProfile | null,
  openSpendingModal: (request: SpendingModalRequest) => void,
  onRetry?: () => void | Promise<void>,
): Promise<boolean> {
  const request = await shouldOpenSpendingModal(err, user);
  if (!request) {
    return false;
  }

  openSpendingModal({
    ...request,
    onSuccess: onRetry
      ? () => {
          void onRetry();
        }
      : undefined,
  });
  return true;
}

export function creditsNeededFromError(err: unknown): number {
  if (!(err instanceof ChuziApiError)) {
    return 1;
  }
  const body = err.body as { credits_needed?: number };
  return typeof body.credits_needed === "number" && body.credits_needed > 0
    ? body.credits_needed
    : 1;
}
